import { useEffect, useRef, useState, type PointerEvent, type KeyboardEvent } from 'react';

export const DEFAULT_SIDEBAR_WIDTH = 260;
const MIN_WIDTH = 220;
const MAX_WIDTH = 360;
const WIDTH_KEY = 'sidebar_width';
const clampWidth = (width: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));

function readWidth() {
  try {
    const saved = Number(localStorage.getItem(WIDTH_KEY));
    return saved > 0 && Number.isFinite(saved) ? clampWidth(saved) : DEFAULT_SIDEBAR_WIDTH;
  } catch {
    return DEFAULT_SIDEBAR_WIDTH;
  }
}

export default function useSidebarResize() {
  const [expandedWidth, setExpandedWidth] = useState(readWidth);
  const [resizing, setResizing] = useState(false);
  const dragStart = useRef<{ x: number; width: number } | null>(null);
  useEffect(() => {
    if (!resizing) return;
    const style = document.createElement('style');
    style.textContent =
      'html, body, body * { cursor: col-resize !important; user-select: none !important; }';
    document.head.appendChild(style);
    return () => style.remove();
  }, [resizing]);
  const updateWidth = (width: number) => {
    const next = clampWidth(width);
    setExpandedWidth(next);
    try {
      localStorage.setItem(WIDTH_KEY, String(next));
    } catch {
      // Resizing remains available when browser storage is unavailable.
    }
  };
  const handleProps = {
    onDoubleClick: () => updateWidth(DEFAULT_SIDEBAR_WIDTH),
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragStart.current = { x: event.clientX, width: expandedWidth };
      setResizing(true);
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => {
      if (!dragStart.current) return;
      updateWidth(dragStart.current.width + event.clientX - dragStart.current.x);
    },
    onPointerUp: (event: PointerEvent<HTMLElement>) => {
      dragStart.current = null;
      setResizing(false);
      event.currentTarget.releasePointerCapture(event.pointerId);
    },
    onLostPointerCapture: () => {
      dragStart.current = null;
      setResizing(false);
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      const widths: Record<string, number> = {
        ArrowLeft: expandedWidth - 8,
        ArrowRight: expandedWidth + 8,
        Home: MIN_WIDTH,
        End: MAX_WIDTH
      };
      if (!(event.key in widths)) return;
      event.preventDefault();
      updateWidth(widths[event.key]);
    }
  };
  return { expandedWidth, resizing, minWidth: MIN_WIDTH, maxWidth: MAX_WIDTH, handleProps };
}
